import React from "react";
import { Column, Conditional, Else, Heading, If, Row, Section, useSync } from "@hex-insights/core";
import { EditMode, Form } from "@hex-insights/forms";
import { useActivePageRegistration } from "@hex-insights/router";
import { Tile, UserDetailQuery, UserField, UserForm, useUserDetailQuery } from "@s-i-s-i/see-learning.shared";
import { Main } from "../../Components";
import { AuthenticationContext } from "../../Contexts";
import { preferencesPageInfo } from "./pageinfo";

export function PreferencesPage() {
	useActivePageRegistration(preferencesPageInfo);

	const { user: authUser } = React.useContext(AuthenticationContext);
	const { data } = useUserDetailQuery({ variables: { id: authUser?.id ?? "0" }, skip: !authUser });
	const user = data?.user;

	const { syncID, synchronize } = useSync();

	return (
		<Main>
			<Heading.H1>Preferences</Heading.H1>

			<Tile style={{ maxWidth: "45rem", margin: "0 auto" }}>
				<Tile.Body>
					<Conditional>
						<If condition={!user}>
							<span>Loading...</span>
						</If>
						<Else>{user && <UserSettingsForm key={syncID} user={user} onSuccess={synchronize} />}</Else>
					</Conditional>
				</Tile.Body>
			</Tile>
		</Main>
	);
}

const userSettingsFields = {
	imageKey: true,
	imageContentType: true,
	givenName: true,
	familyName: true,
	nameOrder: true,
	organizationName: true,
	email: true,
	dateOfBirth: true,
	gender: true,
	pronouns: true,
	// ---
	publicNameFormat: true,
	externalNotificationFrequency: true,
} as const;

type UserSettingsFormProps = {
	user: UserDetailQuery["user"];
	onSuccess: () => void;
};

function UserSettingsForm({ user, onSuccess }: UserSettingsFormProps) {
	const { formState, onSubmit } = UserForm.useDetailForm({
		user,
		onSuccess,
		fields: userSettingsFields,
		editMode: EditMode.WriteOnly,
	});

	return (
		<Form formState={formState} name={`user.viewer.${user.id}`} onSubmit={formState.onSubmitWrapper(onSubmit)}>
			<Section>
				<Section.Header>
					<Heading.H2 noMargin>Personal Details</Heading.H2>
				</Section.Header>
				<Section.Body>
					<Form.Column style={{ width: "100%" }}>
						<UserField.Image formState={formState} style={{ width: "100%" }} />
						<Row justify="spaced-start">
							<UserField.GivenName formState={formState} />
							<UserField.FamilyName formState={formState} />
						</Row>
						<UserField.NameOrder formState={formState} style={{ width: "20rem" }} />
						<UserField.OrganizationName formState={formState} />
						<UserField.Email formState={formState} instanceID={user.id} />
						<UserField.DateOfBirth formState={formState} />
						<UserField.Gender formState={formState} />
						<UserField.Pronouns formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
			<Section>
				<Section.Header>
					<Heading.H2 noMargin>Portal Settings</Heading.H2>
				</Section.Header>
				<Section.Body>
					<Form.Column verticalSpacing="2rem" style={{ width: "100%" }}>
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<strong>Name Display</strong>
							<UserField.PublicNameFormat formState={formState} />
						</Column>
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<strong>Notification Frequency</strong>
							<UserField.ExternalNotificationFrequency formState={formState} />
						</Column>
					</Form.Column>
				</Section.Body>
			</Section>
		</Form>
	);
}
